import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../contexts/ApiContext';
import { format, formatDistanceToNow } from 'date-fns';
import { RefreshCw, Clock, AlertCircle, Play } from 'lucide-react';
import { ApiResponse, CronJobStatus } from '../types';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';

const STATUS_STYLES: Record<string, string> = {
  ok: 'text-green-700 bg-green-50 border-green-200',
  running: 'text-amber-700 bg-amber-50 border-amber-200',
  error: 'text-red-700 bg-red-50 border-red-200',
};

function StatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-md border text-slate-500 bg-slate-50 border-slate-200">Never run</span>;
  }
  const cls = STATUS_STYLES[status] ?? 'text-slate-600 bg-slate-50 border-slate-200';
  const label = status === 'ok' ? 'OK' : status.charAt(0).toUpperCase() + status.slice(1);
  return <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-md border ${cls}`}>{label}</span>;
}

function relative(iso: string | null): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function absolute(iso: string | null): string {
  if (!iso) return '';
  try {
    return format(new Date(iso), "MMM d, yyyy 'at' h:mm a");
  } catch {
    return iso;
  }
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

export default function CronsPage() {
  const { fetchApi } = useApi();
  const [jobs, setJobs] = useState<CronJobStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApi<ApiResponse<CronJobStatus[]>>('/crons');
      if (res.data) setJobs(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cron jobs');
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 3s while any job is running, so a manual trigger's
  // running → ok/error transition shows up without clicking Refresh.
  useEffect(() => {
    if (!jobs.some((j) => j.last_status === 'running')) return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [jobs, load]);

  const runJob = useCallback(async (key: string) => {
    setStarting((prev) => new Set(prev).add(key));
    setError(null);
    try {
      await fetchApi(`/crons/${key}/run`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger job');
    } finally {
      setStarting((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [fetchApi, load]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Scheduled Crons"
        description="Background jobs and their latest run. Schedules are in UTC."
        actions={
          <button onClick={load} disabled={loading} className="btn btn-secondary text-sm">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200 bg-slate-50/50">
                <th className="px-4 py-3">Job</th>
                <th className="px-4 py-3">Schedule</th>
                <th className="px-4 py-3">Last run</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Next run</th>
                <th className="px-4 py-3 text-right">Runs</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && jobs.length === 0 ? (
                <SkeletonRows rows={5} cols={7} />
              ) : jobs.length === 0 ? (
                <tr><td colSpan={7}><EmptyState compact icon={Clock} title="No scheduled jobs" /></td></tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.key} className="hover:bg-slate-50 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                        {job.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 max-w-md">{job.description}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{job.human_schedule}</div>
                      <div className="text-xs font-mono text-slate-400 mt-0.5">{job.schedule}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700" title={absolute(job.last_finished_at ?? job.last_started_at)}>
                        {relative(job.last_finished_at ?? job.last_started_at)}
                      </div>
                      {job.last_duration_ms != null && (
                        <div className="text-xs text-slate-400 mt-0.5">took {formatDuration(job.last_duration_ms)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={job.last_status} />
                      {job.last_status === 'error' && job.last_error && (
                        <div className="text-xs text-red-500 mt-1 max-w-xs truncate" title={job.last_error}>{job.last_error}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700" title={absolute(job.next_run)}>{relative(job.next_run)}</td>
                    <td className="px-4 py-3 text-right text-slate-500">{job.run_count.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      {(() => {
                        const isRunning = job.last_status === 'running' || starting.has(job.key);
                        return (
                          <button
                            onClick={() => runJob(job.key)}
                            disabled={isRunning}
                            className="btn btn-secondary text-xs"
                            title={isRunning ? 'Job is running' : 'Run this job now'}
                          >
                            {isRunning ? (
                              <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                              <Play className="h-3.5 w-3.5 mr-1" />
                            )}
                            {isRunning ? 'Running…' : 'Run now'}
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
